import * as THREE from 'three';
import { CityState } from '../simulation/types';
import { AssetManager } from './AssetManager';

export class TerrainRenderer {
  private scene: THREE.Scene;
  private tileMeshes: Map<string, THREE.Object3D> = new Map();
  private propMeshes: Map<string, THREE.Object3D[]> = new Map();
  private zoneMeshes: Map<string, THREE.Mesh> = new Map();
  private heatmapMeshes: Map<string, THREE.Mesh> = new Map();

  // Translucent colored materials for RCI zones
  private zoneMaterials: Record<string, THREE.MeshBasicMaterial> = {
    residential: new THREE.MeshBasicMaterial({ color: 0x4caf50, transparent: true, opacity: 0.35 }),
    commercial: new THREE.MeshBasicMaterial({ color: 0x2196f3, transparent: true, opacity: 0.35 }),
    industrial: new THREE.MeshBasicMaterial({ color: 0xffeb3b, transparent: true, opacity: 0.35 }),
    office: new THREE.MeshBasicMaterial({ color: 0x00e576, transparent: true, opacity: 0.35 }),
    civic: new THREE.MeshBasicMaterial({ color: 0x9c27b0, transparent: true, opacity: 0.35 }),
    park: new THREE.MeshBasicMaterial({ color: 0x00bcd4, transparent: true, opacity: 0.35 }),
  };

  // Dedicated Heatmap Materials for real-time visualization overlays
  private heatmapMaterials: Record<string, THREE.MeshBasicMaterial> = {
    power: new THREE.MeshBasicMaterial({ color: 0x0288d1, transparent: true, opacity: 0.45 }),
    unpowered: new THREE.MeshBasicMaterial({ color: 0xe53935, transparent: true, opacity: 0.4 }),
    water: new THREE.MeshBasicMaterial({ color: 0x00bcd4, transparent: true, opacity: 0.45 }),
    unwatered: new THREE.MeshBasicMaterial({ color: 0xe53935, transparent: true, opacity: 0.4 }),
    pollution: new THREE.MeshBasicMaterial({ color: 0x795548, transparent: true, opacity: 0.5 }),
    traffic: new THREE.MeshBasicMaterial({ color: 0xff5722, transparent: true, opacity: 0.5 }),
    crime: new THREE.MeshBasicMaterial({ color: 0x673ab7, transparent: true, opacity: 0.45 }),
    fire: new THREE.MeshBasicMaterial({ color: 0xff9800, transparent: true, opacity: 0.45 }),
    landvalue: new THREE.MeshBasicMaterial({ color: 0x4caf50, transparent: true, opacity: 0.45 }),
  };

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  public render(state: CityState) {
    const terrain = state.terrain;
    const width = state.width;
    const height = state.height;

    const assetMgr = AssetManager.getInstance();

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const type = terrain[y][x];
        const key = `${x},${y}`;

        // Map grid coordinate to 3D world space (centered around 0,0,0)
        const worldX = x - width / 2 + 0.5;
        const worldZ = y - height / 2 + 0.5;

        // 1. Render Terrain base block
        if (!this.tileMeshes.has(key)) {
          const mesh = assetMgr.getTerrainMesh(type);
          mesh.position.set(worldX, 0, worldZ);
          this.scene.add(mesh);
          this.tileMeshes.set(key, mesh);
        }

        // 2. Hide/show trees depending on whether there is a building or road on this cell
        const hasConstruction = !!state.buildings[key] || !!state.roads[key];
        
        // Spawn trees if grassy cell and no construction
        this.decorateNature(x, y, type, worldX, worldZ, key, hasConstruction);
      }
    }

    // Render painted zone overlays
    this.updateZones(state);
  }

  private decorateNature(
    x: number,
    y: number,
    type: number,
    worldX: number,
    worldZ: number,
    key: string,
    hasConstruction: boolean
  ) {
    // Only grass gets trees
    if (type !== 2) return;

    // Retrieve or generate tree group
    if (!this.propMeshes.has(key)) {
      const seed = Math.sin(x * 12.9898 + y * 78.233) * 43758.5453;
      const random = seed - Math.floor(seed);

      // 12% probability of tree cluster
      if (random < 0.12) {
        const trees: THREE.Object3D[] = [];
        const count = (Math.floor(random * 100) % 3) + 1; // 1 to 3 trees
        const assetMgr = AssetManager.getInstance();

        for (let i = 0; i < count; i++) {
          // Semi-random displacements inside the 1x1 cell boundaries
          const offsetX = Math.sin(random * (i + 1) * 5) * 0.28;
          const offsetZ = Math.cos(random * (i + 1) * 3) * 0.28;

          let treeType: 'oak' | 'pine' | 'sakura' | 'palm' = 'oak';
          if (x <= 4 || y <= 4 || x >= 27 || y >= 27) {
            treeType = 'palm'; // shoreline coasts
          } else {
            const randVal = (x * 7 + y * 13 + i) % 3;
            if (randVal === 0) treeType = 'oak';
            else if (randVal === 1) treeType = 'pine';
            else treeType = 'sakura';
          }

          const treeMesh = assetMgr.getVegetationMesh(treeType);
          treeMesh.position.set(worldX + offsetX, 0, worldZ + offsetZ);
          this.scene.add(treeMesh);
          trees.push(treeMesh);
        }

        this.propMeshes.set(key, trees);
      }
    }

    // Set visibility of props
    const props = this.propMeshes.get(key);
    if (props) {
      props.forEach((tree) => {
        tree.visible = !hasConstruction;
      });
    }
  }

  /**
   * Refreshes translucent overlay rectangles to mark zoning under buildings
   */
  public updateZones(state: CityState) {
    const width = state.width;
    const height = state.height;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const key = `${x},${y}`;
        const parcel = state.parcels[key];
        const hasBuilding = !!state.buildings[key];
        const hasRoad = !!state.roads[key];

        // Only draw zones if zoned AND no building/road sits on top, matching Pocket City perfectly
        const shouldShowZone = parcel && !hasBuilding && !hasRoad;

        if (shouldShowZone) {
          const zoneMat = this.zoneMaterials[parcel.zone];
          if (zoneMat) {
            const worldX = x - width / 2 + 0.5;
            const worldZ = y - height / 2 + 0.5;

            let overlay = this.zoneMeshes.get(key);
            if (!overlay) {
              const geo = new THREE.BoxGeometry(0.85, 0.005, 0.85); // thin flat sheet sitting at Y=0.005
              overlay = new THREE.Mesh(geo, zoneMat);
              overlay.position.set(worldX, 0.005, worldZ);
              this.scene.add(overlay);
              this.zoneMeshes.set(key, overlay);
            } else {
              overlay.material = zoneMat;
              overlay.visible = true;
            }
          }
        } else {
          const overlay = this.zoneMeshes.get(key);
          if (overlay) {
            overlay.visible = false;
          }
        }
      }
    }
  }

  /**
   * Renders real-time translucent colored face blocks over the terrain to display the selected heatmap.
   */
  public updateHeatmaps(state: CityState, activeOverlay: string) {
    const width = state.width;
    const height = state.height;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const key = `${x},${y}`;
        const b = state.buildings[key];
        const isRoad = !!state.roads[key];

        let shouldShow = activeOverlay !== 'none';
        let mat = this.heatmapMaterials.power; // default fallback
        let opacity = 0.45;

        if (shouldShow) {
          if (activeOverlay === 'power') {
            // Powered tiles show blue, unpowered RCI/civics show red, unzoned grass is transparent
            const isZonedOrCivic = !!state.parcels[key] || !!b;
            if (isZonedOrCivic) {
              const hasPower = !!state.powerGrid[key] || (b && b.zone === 'civic' && b.familyId.includes('power'));
              mat = hasPower ? this.heatmapMaterials.power : this.heatmapMaterials.unpowered;
            } else {
              shouldShow = false;
            }
          } else if (activeOverlay === 'water') {
            const isZonedOrCivic = !!state.parcels[key] || !!b;
            if (isZonedOrCivic) {
              const hasWater = !!state.waterGrid[key] || (b && b.zone === 'civic' && b.familyId.includes('water'));
              mat = hasWater ? this.heatmapMaterials.water : this.heatmapMaterials.unwatered;
            } else {
              shouldShow = false;
            }
          } else if (activeOverlay === 'pollution') {
            const poll = state.pollutionGrid[key] || 0;
            if (poll > 0) {
              mat = this.heatmapMaterials.pollution;
              opacity = Math.max(0.15, Math.min(0.75, poll / 100)); // opacity maps density
            } else {
              shouldShow = false;
            }
          } else if (activeOverlay === 'traffic') {
            const traff = state.trafficGrid[key] || 0;
            if (isRoad && traff > 0) {
              mat = this.heatmapMaterials.traffic;
              opacity = Math.max(0.2, Math.min(0.8, traff / 100));
            } else {
              shouldShow = false;
            }
          } else if (activeOverlay === 'crime') {
            const crime = state.crimeGrid[key] || 0;
            if (crime > 10) {
              mat = this.heatmapMaterials.crime;
              opacity = Math.max(0.15, Math.min(0.75, crime / 100));
            } else {
              shouldShow = false;
            }
          } else if (activeOverlay === 'fire') {
            const fire = state.fireGrid[key] || 0;
            if (fire > 10) {
              mat = this.heatmapMaterials.fire;
              opacity = Math.max(0.15, Math.min(0.75, fire / 100));
            } else {
              shouldShow = false;
            }
          } else if (activeOverlay === 'landvalue') {
            const lv = state.landValueGrid[key] || 100;
            mat = this.heatmapMaterials.landvalue;
            // Map 100 Baseline to 0.45 opacity, 250 max value to 0.8 opacity
            opacity = Math.max(0.15, Math.min(0.85, (lv - 20) / 200));
          } else {
            shouldShow = false;
          }
        }

        if (shouldShow) {
          const worldX = x - width / 2 + 0.5;
          const worldZ = y - height / 2 + 0.5;

          let overlay = this.heatmapMeshes.get(key);
          if (!overlay) {
            // Sit slightly above RCI zone sheet (Y=0.005) to avoid Z-fighting
            const geo = new THREE.BoxGeometry(0.88, 0.005, 0.88);
            overlay = new THREE.Mesh(geo, mat.clone());
            overlay.position.set(worldX, 0.008, worldZ);
            this.scene.add(overlay);
            this.heatmapMeshes.set(key, overlay);
          } else {
            // Update material and visibility
            overlay.material = mat.clone();
            (overlay.material as THREE.MeshBasicMaterial).opacity = opacity;
            overlay.visible = true;
          }
        } else {
          const overlay = this.heatmapMeshes.get(key);
          if (overlay) {
            overlay.visible = false;
          }
        }
      }
    }
  }

  public setPropVisibility(x: number, y: number, visible: boolean) {
    const key = `${x},${y}`;
    const props = this.propMeshes.get(key);
    if (props) {
      props.forEach((p) => {
        p.visible = visible;
      });
    }
  }

  public clear() {
    this.tileMeshes.forEach((mesh) => this.scene.remove(mesh));
    this.tileMeshes.clear();

    this.propMeshes.forEach((trees) => {
      trees.forEach((tree) => this.scene.remove(tree));
    });
    this.propMeshes.clear();

    this.zoneMeshes.forEach((mesh) => this.scene.remove(mesh));
    this.zoneMeshes.clear();

    this.heatmapMeshes.forEach((mesh) => this.scene.remove(mesh));
    this.heatmapMeshes.clear();
  }
}
